import { Injectable, Injector } from "@angular/core";
import { SwPush } from "@angular/service-worker";
import { distinctUntilChanged, map, mergeMap, startWith, take, tap } from "rxjs/operators";
import { LoggingService } from "../../../shared/services/core/logging.service";
import { ApiService } from "../../../shared/services/core/api.service";
import { WebPushNotification } from "./models/web-push.model";
import { DeviceDetectorService } from "ngx-device-detector";
import { Observable, combineLatest, iif, of } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { config } from "../../../shared/config";
import { NotificationCenterQuery } from "../notification-center/notification-center.query";

/**
 * Service to handle web push notifications
 */
@Injectable({ providedIn: 'root' })
export class WebPushNotificationService {

    private swPush: SwPush;
    private notification: NotificationCenterQuery;
    isEnabled: boolean
    isSubscribed$: Observable<boolean>;
    isAppBadgeSupported: boolean = false;

    constructor(private api: ApiService, 
                private http: HttpClient,
                private logger: LoggingService,
                private injector: Injector,
                private deviceService: DeviceDetectorService){
          this.swPush = this.injector.get(SwPush, undefined);
          this.notification = this.injector.get(NotificationCenterQuery);
          this.isEnabled = this.swPush.isEnabled;
          this.isSubscribed$ = this.swPush.subscription.pipe(map((sub) => !!sub), distinctUntilChanged());
          this.isAppBadgeSupported = 'setAppBadge' in navigator;
    }
    
    /**
     * Save the subscription details to the database
     */
    private _subscribeToWebPushNotifications(wb: WebPushNotification) {
      return this.api.post('webpush', wb);
    }

    /**
     * Remove the subscription details from the database then unsubscribe from the browser
     * 
     */
    private _unsubscribeToWebPushNotifications(key: string) {
      return this.http.delete(`${config.baseApiUrl}/api/webpush`, { headers: this.api.getHeaders(), params: { Key: key } });
    }

    /**
     * Log that the user has received a notification
     */
    logMessageReceived(id: string){
      return this.api.post(`webpush/messageRecieved/${id}`, {});
    }

    /**
     * Subscribe to webpush notifications, update the badge count and log the message to the database if Id is found
     */
    subscribe(next?: any, error?: any, complete?: any): void{
      combineLatest([
        // Update the badge count when the notification count changes, startWith pipe so that upon opening the app, the badge count is set ex. when app is exited then re-opened
        // Only works when app is installed and app is opened or minimized in the background, badge wont update if app is totally closed
        this.notification.unseenNotificationsCount$.pipe(startWith(this.notification.getValue().unseenCount || 0), tap(count => this.setAppBadge(count))),
        // Subscribe to the messages and log the message to the database if Id is found
        this.swPush.messages.pipe(mergeMap((message: any) => iif(() => !!message.Id, this.logMessageReceived(message.Id).pipe(map((message => message))), of(message))))
      ]).pipe(
        map(([notificationCount, message]) => ({ notificationCount, message }))
      ).subscribe({ next, error, complete });
    }

    /**
     * Log the reason why the user denied permission to send notifications
     */
    private logDenyPermission(reason: string): Observable<any>{
      return this.api.post('webpush/reject', null, { reason } as any);
    }

    /**
     * Ask the user for permission to send notifications and subscribe to the notifications then save the subscription details to the database
     */
    requestSubscription(publicKey: string): Promise<any> {
      return new Promise((resolve, reject) => {

        if (!this.swPush.isEnabled) {
            throw new Error("Notification is not enabled.");
        }

        this.swPush.requestSubscription({
            serverPublicKey: publicKey
        }).then((sub: any) => {
            this.logger.info('Notification Subscription: ', sub.toJSON());

            let json = sub.toJSON();
            const key = json.keys?.p256dh || '';
            const auth = json.keys?.auth || '';

            const webPush: WebPushNotification = {
                Auth: auth,
                P256DH: key,
                Endpoint: sub.endpoint,
                ExpirationTime: sub.expirationTime,
                PublicKey: publicKey,
                DeviceInfo: JSON.stringify(this.deviceService.getDeviceInfo())
            };

            // Save the subscription details to the database
            this._subscribeToWebPushNotifications(webPush).toPromise().then(
              (res) => {
                // Once subscribed to webpush notifications, we can set the app icon badge count
                // Note: This badge count only works when app is installed and does not apply in normal browser
                // Tested on iPhone and Macbook, Windows if installed
                const count = this.notification.getValue().unseenCount || 0;
                this.setAppBadge(count);
                resolve(res);
              },
              (reason) => {
                this.logger.error('Could not save subscription details to the database', reason);
                // Unsubscribe to the browser locally if database save fails
                this.swPush.unsubscribe();
                reject(reason);
              }
            );
                    
        }).catch((err) => {  
            this.logger.error('Could not subscribe to notifications', err);
            // Log to the database that the user denied permission
            this.logDenyPermission('denied').toPromise().then(resolve, reject);
        });
      });
    }

    /**
     * Unsubscribe user from the notifications and remove the subscription details from the database
     */
    unsubscribe(): Promise<any>{
      return new Promise(async(resolve, reject) => {

        const pushSubscription = await this.swPush.subscription.pipe(take(1)).toPromise();
            
        if( !pushSubscription ){
            throw new Error('The user was never subscribed');
        }else{
            // Retrieve the key from the subscription
            const key = pushSubscription.toJSON().keys?.p256dh || '';

            // Unsubscribe from the database using the key
            this._unsubscribeToWebPushNotifications(key).toPromise()
            .then(() => {
              // Once we are unsubscribed from webpush notifications, we can clear the app badge count
              this.clearAppBadge();
              this.logger.info('Successfully unsubscribed to notifications');
              // Unsubscribe from the browser locally
              this.swPush.unsubscribe().then(resolve, reject);
            }).catch((err) => {
              this.logger.error('Could not unsubscribe to notifications', err);
              reject(err);
            });
        }
      });
    }

    /**
     * Set the app icon badge count
     */
    setAppBadge(count: number): void{
      // Check if app badge is supported
      if( this.isAppBadgeSupported ){
        // Set the app icon badge count
        (navigator as any).setAppBadge(count);
      }
    }

    /**
     * Clear the app icon badge count
     */
    clearAppBadge(): void{
      if( this.isAppBadgeSupported ){
        (navigator as any).setAppBadge(0);
      }
    }

}